import type { TaxInvoice } from '@/types/gst';
import { comingSoon } from './orderPresentation';

export const GST_PATH = '/customer/settings/gst';
export const GST_EDIT_PATH = `${GST_PATH}/edit`;
export const invoicePath = (id: string) => `${GST_PATH}/invoices/${id}`;

/** Opens the invoice PDF when the API provides one. */
export const downloadInvoice = (invoice: TaxInvoice) =>
  invoice.pdfUrl ? window.open(invoice.pdfUrl, '_blank', 'noopener') : comingSoon('Downloading invoices');

// GSTIN: 2-digit state code, 10-character PAN, entity number, "Z", check character.
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

/** Upper-cases and strips spaces as the customer types. */
export const normalizeGstin = (value: string) => value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15);

/** Why a GSTIN can't be right as typed (G2), or undefined when the format is fine. */
export function gstinFormatError(gstin: string): string | undefined {
  if (gstin.length !== 15) return 'GSTIN must be 15 characters, like 27ABCDE1234F1Z5';
  if (!GSTIN_PATTERN.test(gstin)) return 'This isn’t a valid GSTIN — check it against your GST certificate';
  return undefined;
}

/** "Maharashtra (27)" from a GSTIN and its state name. */
export const placeOfSupply = (gstin: string, stateName: string) => `${stateName} (${gstin.slice(0, 2)})`;

const ONES = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function belowHundred(n: number) {
  return n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? `-${ONES[n % 10]}` : ''}`;
}

function belowThousand(n: number) {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return [h && `${ONES[h]} hundred`, rest && belowHundred(rest)].filter(Boolean).join(' ');
}

/** Indian-system amount in words for invoices: 899 → "Eight hundred ninety-nine rupees only". */
export function rupeesInWords(amount: number): string {
  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);
  const parts: string[] = [];
  const crore = Math.floor(rupees / 1e7);
  const lakh = Math.floor((rupees % 1e7) / 1e5);
  const thousand = Math.floor((rupees % 1e5) / 1e3);
  const rest = rupees % 1e3;
  if (crore) parts.push(`${belowThousand(crore)} crore`);
  if (lakh) parts.push(`${belowHundred(lakh)} lakh`);
  if (thousand) parts.push(`${belowHundred(thousand)} thousand`);
  if (rest) parts.push(belowThousand(rest));
  const words = `${parts.join(' ') || 'zero'} rupees${paise ? ` and ${belowHundred(paise)} paise` : ''} only`;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "₹761.86" — invoices always show paise. */
export const formatInvoiceAmount = (amount: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount);
